import type { Dispatch } from 'redux';
import { fetchModuleActivations, fetchModuleLicenses } from '@/uptk/core/components/marketplace/api';
import { getPreferredAccessToken } from '@/uptk/core/api/clerkTokenProvider';
import {
  buildModuleEntitlementSnapshot,
  saveModuleEntitlementSnapshot,
  type ModuleEntitlementSnapshot,
} from '@/uptk/core/entitlements/module_entitlement_storage';

export const MODULE_ENTITLEMENTS_LOADING = 'MODULE_ENTITLEMENTS_LOADING';
export const MODULE_ENTITLEMENTS_LOADED = 'MODULE_ENTITLEMENTS_LOADED';
export const MODULE_ENTITLEMENTS_ERROR = 'MODULE_ENTITLEMENTS_ERROR';
export const MODULE_ENTITLEMENTS_RESET = 'MODULE_ENTITLEMENTS_RESET';

export type ModuleEntitlementsLoadingAction = {
  type: typeof MODULE_ENTITLEMENTS_LOADING;
};

export type ModuleEntitlementsLoadedAction = {
  type: typeof MODULE_ENTITLEMENTS_LOADED;
  payload: ModuleEntitlementSnapshot;
};

export type ModuleEntitlementsErrorAction = {
  type: typeof MODULE_ENTITLEMENTS_ERROR;
  error: string;
};

export type ModuleEntitlementsResetAction = {
  type: typeof MODULE_ENTITLEMENTS_RESET;
};

export type ModuleEntitlementsAction =
  | ModuleEntitlementsLoadingAction
  | ModuleEntitlementsLoadedAction
  | ModuleEntitlementsErrorAction
  | ModuleEntitlementsResetAction;

const getTenantId = (tenantId?: string) => {
  if (typeof window === 'undefined') {
    return tenantId ?? '';
  }
  const stored = localStorage.getItem('tenantid') ?? '';
  return (tenantId ?? stored).trim();
};

const waitForAccessToken = async (attempts = 6, delayMs = 400) => {
  for (let attempt = 0; attempt < attempts; attempt += 1) {
    const token = await getPreferredAccessToken();
    if (token) {
      return token;
    }
    await new Promise((resolve) => setTimeout(resolve, delayMs));
  }
  return null;
};

export const loadModuleEntitlements = (options?: { tenantId?: string }) => {
  return async (dispatch: Dispatch<ModuleEntitlementsAction>) => {
    const tenantId = getTenantId(options?.tenantId);
    if (!tenantId) {
      dispatch({ type: MODULE_ENTITLEMENTS_ERROR, error: 'Missing tenant id.' });
      return;
    }
    const token = await waitForAccessToken();
    if (!token) {
      return;
    }
    dispatch({ type: MODULE_ENTITLEMENTS_LOADING });
    try {
      const [activations, licenses] = await Promise.all([
        fetchModuleActivations({ tenantId }),
        fetchModuleLicenses({ tenantId }),
      ]);
      const snapshot = buildModuleEntitlementSnapshot(tenantId, activations, licenses);
      saveModuleEntitlementSnapshot(snapshot);
      dispatch({ type: MODULE_ENTITLEMENTS_LOADED, payload: snapshot });
    } catch (error) {
      dispatch({ type: MODULE_ENTITLEMENTS_ERROR, error: 'Failed to load module entitlements.' });
    }
  };
};

export const resetModuleEntitlements = (): ModuleEntitlementsResetAction => ({
  type: MODULE_ENTITLEMENTS_RESET,
});
